import React, { useState, useRef } from "react";
import { parseSchema, detectSchemaType } from "../../../utils/schemaParser";
import styles from "./style.module.css";

// Sample OpenAPI Schema
const SAMPLE_OPENAPI = `{
  "openapi": "3.0.0",
  "info": {
    "title": "Sample Pet Store API",
    "version": "1.0.0",
    "description": "A sample API for managing pets"
  },
  "paths": {
    "/pets": {
      "get": {
        "operationId": "listPets",
        "summary": "List all pets",
        "tags": ["pets"],
        "responses": {
          "200": {
            "description": "A list of pets",
            "content": {
              "application/json": {
                "schema": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "id": { "type": "integer" },
                      "name": { "type": "string" },
                      "species": { "type": "string" }
                    }
                  }
                }
              }
            }
          }
        }
      },
      "post": {
        "operationId": "createPet",
        "summary": "Create a new pet",
        "tags": ["pets"],
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "properties": {
                  "name": { "type": "string" },
                  "species": { "type": "string" }
                }
              }
            }
          }
        },
        "responses": {
          "201": { "description": "Pet created successfully" }
        }
      }
    },
    "/pets/{petId}": {
      "get": {
        "operationId": "getPetById",
        "summary": "Get a pet by ID",
        "tags": ["pets"],
        "parameters": [
          {
            "name": "petId",
            "in": "path",
            "required": true,
            "schema": { "type": "integer" }
          }
        ],
        "responses": {
          "200": { "description": "Pet details" }
        }
      },
      "delete": {
        "operationId": "deletePet",
        "summary": "Delete a pet",
        "tags": ["pets"],
        "parameters": [
          {
            "name": "petId",
            "in": "path",
            "required": true,
            "schema": { "type": "integer" }
          }
        ],
        "responses": {
          "204": { "description": "Pet deleted" }
        }
      }
    },
    "/users": {
      "get": {
        "operationId": "listUsers",
        "summary": "List all users",
        "tags": ["users"],
        "responses": {
          "200": { "description": "A list of users" }
        }
      }
    }
  }
}`;

// Sample GraphQL Schema
const SAMPLE_GRAPHQL = `type Query {
  users: [User!]!
  user(id: ID!): User
  posts: [Post!]!
  post(id: ID!): Post
}

type Mutation {
  createUser(name: String!, email: String!): User!
  updateUser(id: ID!, name: String, email: String): User
  deleteUser(id: ID!): Boolean!
  createPost(title: String!, content: String!, authorId: ID!): Post!
}

type User {
  id: ID!
  name: String!
  email: String!
  posts: [Post!]!
  createdAt: String!
}

type Post {
  id: ID!
  title: String!
  content: String!
  author: User!
  published: Boolean!
  createdAt: String!
}`;

const SchemaImport = ({ onSchemaImported }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [schemaType, setSchemaType] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const fileContent = event.target.result;
      setContent(fileContent);
      const detected = detectSchemaType(fileContent);
      setSchemaType(detected);
      setIsLoading(false);
    };
    reader.onerror = () => {
      setError("Failed to read file");
      setIsLoading(false);
    };
    reader.readAsText(file);
  };

  const handlePaste = (e) => {
    const pastedContent = e.target.value;
    setContent(pastedContent);
    if (pastedContent.trim()) {
      const detected = detectSchemaType(pastedContent);
      setSchemaType(detected);
    } else {
      setSchemaType(null);
    }
  };

  const handleImport = () => {
    if (!content.trim()) {
      setError("Please provide a schema");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const parsedSchema = parseSchema(content);
      onSchemaImported(parsedSchema);
      setIsOpen(false);
      setContent("");
      setSchemaType(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileContent = event.target.result;
        setContent(fileContent);
        const detected = detectSchemaType(fileContent);
        setSchemaType(detected);
      };
      reader.readAsText(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const getSchemaTypeLabel = () => {
    switch (schemaType) {
      case "graphql":
        return "GraphQL SDL";
      case "openapi-json":
        return "OpenAPI (JSON)";
      case "openapi-yaml":
        return "OpenAPI (YAML)";
      default:
        return "Unknown";
    }
  };

  const loadSampleSchema = (type) => {
    const sample = type === "openapi" ? SAMPLE_OPENAPI : SAMPLE_GRAPHQL;
    setContent(sample);
    const detected = detectSchemaType(sample);
    setSchemaType(detected);
  };

  return (
    <div className={styles.container}>
      <button
        className={styles.importButton}
        onClick={() => setIsOpen(true)}
      >
        + Import Schema
      </button>

      {isOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Import API Schema</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Quick Start with Sample Schemas */}
              <div className={styles.sampleSection}>
                <label>Quick Start - Load Sample Schema:</label>
                <div className={styles.sampleButtons}>
                  <button
                    className={styles.sampleButton}
                    onClick={() => loadSampleSchema("openapi")}
                  >
                    Sample REST (Pet Store)
                  </button>
                  <button
                    className={styles.sampleButton}
                    onClick={() => loadSampleSchema("graphql")}
                  >
                    Sample GraphQL (Users & Posts)
                  </button>
                </div>
              </div>

              <div className={styles.divider}>
                <span>OR UPLOAD</span>
              </div>

              <div
                className={styles.dropZone}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.yaml,.yml,.graphql,.gql"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
                <div className={styles.dropZoneContent}>
                  <span className={styles.uploadIcon}>📁</span>
                  <p>Drop a file here or click to upload</p>
                  <small>Supports: OpenAPI (JSON/YAML), GraphQL SDL</small>
                </div>
              </div>

              <div className={styles.divider}>
                <span>OR PASTE</span>
              </div>

              <div className={styles.pasteSection}>
                <textarea
                  className={styles.textarea}
                  value={content}
                  onChange={handlePaste}
                  placeholder="Paste your OpenAPI (JSON/YAML) or GraphQL SDL here..."
                  rows={10}
                />
              </div>

              {schemaType && (
                <div className={styles.detectedType}>
                  Detected: <strong>{getSchemaTypeLabel()}</strong>
                </div>
              )}

              {error && (
                <div className={styles.error}>
                  {error}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.cancelButton}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={handleImport}
                disabled={isLoading || !content.trim()}
              >
                {isLoading ? "Parsing..." : "Import Schema"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaImport;
